<script setup lang="ts">
import type { Component } from "vue";
import { computed, ref } from "vue";
import { IComponentInputEmits, IComponentInputProps } from "@/types/main";
import { vMaska } from "maska/vue";

const props = withDefaults(defineProps<IComponentInputProps>(), {
  modelValue: "",
  label: "Input",
  placeholder: "",
  type: "text",
  disabled: false,
  error: false,
  errorMessage: "",
  showCounter: false,
  multiline: false,
  rows: 3,
  tooManyMessage: "Too many symbols",
  autocomplete: "",
});

const emit = defineEmits<IComponentInputEmits>();

const isFocused = ref(false);

const lengthNow = computed(() => String(props.modelValue ?? "").length);

const isTooLong = computed(() => {
  if (!props.maxLength) return false;
  return lengthNow.value > props.maxLength;
});

const isActive = computed(
  () =>
    isFocused.value ||
    (props.modelValue !== "" &&
      props.modelValue !== null &&
      props.modelValue !== undefined)
);

const isDisabledFilled = computed(() => props.disabled && lengthNow.value > 0);
const hasError = computed(() => props.error || isTooLong.value);

const finalErrorMessage = computed(() => {
  if (isTooLong.value) return props.tooManyMessage;
  return props.errorMessage;
});

const counterText = computed(() => {
  if (!props.showCounter || !props.maxLength) return "";
  return `${lengthNow.value}/${props.maxLength}`;
});

function onInput(e: Event) {
  const input = e.target as HTMLInputElement;
  let val = input.value;

  if (!props.mask && props.type !== "number") {
    val = val.replace(/^\s+/, "");
    val = val.replace(/\s{2,}/g, " ");
    if (input.value !== val) input.value = val;
  }

  emit("update:modelValue", val);
}

function onMaska(e: Event) {
  const detail = (e as CustomEvent).detail;
  emit(
    "update:modelValue",
    detail?.masked ?? (e.target as HTMLInputElement).value
  );
  emit("mask:completed", !!detail?.completed);
}

function onFocus() {
  isFocused.value = true;
  emit("focus");
}

function onBlur() {
  isFocused.value = false;
  emit("blur");
}

const appendIconClick = () => {
  emit("appendIconClick");
};
</script>

<template>
  <div
    class="c-input-wrap"
    :style="props.width ? { minWidth: props.width } : {}"
  >
    <div
      class="c-input"
      :class="{
        'is-active': isActive,
        'is-error': hasError,
        'is-too-long': isTooLong,
        'is-disabled': disabled,
        'is-lg': multiline,
        'is-disabled-filled': isDisabledFilled,
      }"
    >
      <div v-if="icon" class="icon">
        <component :is="icon" :size="20" />
      </div>

      <div class="field">
        <span class="floating-label">
          {{ label }}
        </span>

        <textarea
          v-if="multiline"
          class="control"
          :rows="rows"
          :placeholder="isActive ? placeholder : ''"
          :disabled="disabled"
          :value="modelValue"
          @input="onInput"
          @focus="onFocus"
          @blur="onBlur"
        />
        <input
          v-else
          class="control"
          v-maska="mask"
          :type="type === 'number' ? 'text' : type ?? 'text'"
          :inputmode="type === 'number' ? 'decimal' : undefined"
          :placeholder="isActive ? placeholder : ''"
          :disabled="disabled"
          :value="modelValue"
          :name="label"
          :autocomplete="autocomplete"
          spellcheck="false"
          @input="onInput"
          @focus="onFocus"
          @blur="onBlur"
          @maska="onMaska"
        />
      </div>
      <div v-if="appendIcon" class="append-icon" @click="appendIconClick">
        <component :is="appendIcon" :size="20" />
      </div>
    </div>

    <div v-if="showCounter || finalErrorMessage" class="meta">
      <div v-if="error && finalErrorMessage" class="error-text">
        {{ finalErrorMessage }}
      </div>
      <div v-if="counterText" class="counter">
        {{ counterText }}
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.c-input-wrap {
  min-width: 340px;
}

.c-input {
  display: flex;
  align-items: center;
  gap: 8px;

  height: 52px;
  padding: 10px 14px;
  background: var(--color-secondary-system-background);
  border: 1px solid var(--color-border);
  border-radius: 15px;
  transition: 0.15s ease;

  &.is-disabled {
    pointer-events: none;
  }

  &.is-error {
    border-color: var(--color-red-lighter);
    background: var(--color-red-lighter);

    .icon {
      color: var(--color-system-red);
    }

    .floating-label {
      color: var(--color-system-red);
    }
  }

  &.is-lg {
    height: auto;
    align-items: flex-start;
  }

  &.is-disabled-filled .control {
    color: var(--color-label-secondary);
  }

  &.is-too-long .control {
    color: var(--color-system-red);
  }
}

.icon {
  display: flex;
  align-items: center;
  color: var(--color-label-secondary);
}

.append-icon {
  display: flex;
  align-items: center;
  color: var(--color-label-secondary);
  cursor: pointer;
  background: transparent;
}

.field {
  position: relative;
  width: 100%;
  background: transparent;
}

.floating-label {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: var(--color-label-secondary);
  pointer-events: none;
  font-weight: 500;
  line-height: 20px;
  transition: 0.25s ease;
  background: transparent;
}

.c-input.is-lg .floating-label {
  top: 12px;
  transform: translateY(-50%);
}

.c-input.is-active:not(.is-lg) .floating-label {
  top: -3px;
  font-size: 12px;
  transform: translateY(0) scale(0.92);
}

.c-input.is-lg.is-active .floating-label {
  opacity: 0;
  visibility: hidden;
  transform: translateY(-50%);
}

.control {
  width: 100%;
  border: none;
  outline: none;
  background: transparent;

  font-weight: 600;
  font-size: 14px;
  line-height: 20px;

  color: var(--color-label);

  padding-top: 14px;
  padding-bottom: 2px;

  &::placeholder {
    font-size: 16px;
    font-weight: 600;
    color: var(--color-label-secondary);
  }
}

.c-input.is-lg .control {
  padding-top: 0px;
}

.c-input:not(.is-active):not(.is-lg) .control {
  padding-top: 2px;
  padding-bottom: 2px;
}

textarea.control {
  resize: none;
  min-height: 68px;
}

.meta {
  margin-top: 6px;
  margin-bottom: 6px;
  display: flex;
  justify-content: space-between;
  padding: 0 12px;
}

.error-text {
  font-size: 12px;
  line-height: 16px;
  color: var(--color-system-red);
}

.counter {
  font-size: 12px;
  line-height: 16px;
  color: var(--color-label-secondary);
}

.c-input.is-error ~ .meta .counter,
.c-input.is-too-long ~ .meta .counter {
  color: var(--color-system-red);
}
</style>
